import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getClient,
  updateClient,
  getLetters,
  getDisputes,
  getDocuments,
  getCreditReports,
} from "../api";

const IDENTITYIQ_LINK =
  
"https://member.identityiq.com/sc-securemax.aspx?offercode=431275W6&utm_medium=social&utm_source=linktree&utm_campaign=get+all+3+credit+reports+for+$1+equifax/experian/transunion&utm_content=link_in_bio";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  date_of_birth: "",
  ssn_last4: "",
  notes: "",
};

function normalizeClient(raw) {
  if (!raw) return null;

  const firstName = raw.first_name || "";
  const lastName = raw.last_name || "";

  return {
    ...raw,
    id: raw.id ?? raw.client_id ?? raw.clientId ?? null,
    first_name: firstName,
    last_name: lastName,
    full_name:
      raw.full_name ||
      raw.name ||
      `${firstName} ${lastName}`.trim(),
    email: raw.email || "",
    phone: raw.phone || "",
    address: raw.address || "",
    city: raw.city || "",
    state: raw.state || "",
    zip_code: raw.zip_code || raw.zip || "",
    date_of_birth:
      raw.date_of_birth ||
      raw.birthday ||
      "",
    ssn_last4: raw.ssn_last4 || "",
    notes: raw.notes || "",
  };
}

function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled = false,
  style = {},
}) {
  const variants = {
    primary: {
      background: "linear-gradient(135deg, #ea580c, #f97316)",
      color: "white",
      border: "none",
    },
    secondary: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    ghost: {
      background: "white",
      color: "#334155",
      border: "1px solid #cbd5e1",
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "12px 18px",
        borderRadius: "14px",
        fontSize: "14px",
        fontWeight: "800",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.65 : 1,
        transition: "0.2s ease",
        ...variants[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  maxLength,
}) {
  return (
    <label style={{ display: "grid", gap: "8px" }}>
      <span
        style={{
          fontSize: "14px",
          fontWeight: "800",
          color: "#334155",
        }}
      >
        {label}
      </span>

      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "13px 14px",
          borderRadius: "14px",
          border: "1px solid #cbd5e1",
          background: "white",
          color: "#0f172a",
          fontSize: "15px",
          outline: "none",
        }}
      />
    </label>
  );
}

function StatCard({ title, value }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "20px",
        padding: "24px",
      }}
    >
      <div
        style={{
          fontSize: "15px",
          fontWeight: "700",
          color: "#64748b",
          marginBottom: "10px",
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: "42px",
          fontWeight: "900",
          color: "#0f172a",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function ClientDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [client, setClient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [stats, setStats] = useState({
    letters: 0,
    disputes: 0,
    documents: 0,
    creditReports: 0,
  });

  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const applyClientToForm = useCallback((normalized) => {
    setForm({
      first_name: normalized?.first_name || "",
      last_name: normalized?.last_name || "",
      email: normalized?.email || "",
      phone: normalized?.phone || "",
      address: normalized?.address || "",
      city: normalized?.city || "",
      state: normalized?.state || "",
      zip_code: normalized?.zip_code || "",
      date_of_birth: normalized?.date_of_birth || "",
      ssn_last4: normalized?.ssn_last4 || "",
      notes: normalized?.notes || "",
    });
  }, []);

  const loadPage = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError("");

      const [
        clientData,
        lettersData,
        disputesData,
        documentsData,
        reportsData,
      ] = await Promise.all([
        getClient(id),
        getLetters(),
        getDisputes(),
        getDocuments(),
        getCreditReports(),
      ]);

      const normalized = normalizeClient(clientData);
      setClient(normalized);
      applyClientToForm(normalized);

      const letters = Array.isArray(lettersData)
        ? lettersData.filter(
            (item) => String(item.client_id) === String(id)
          )
        : [];

      const disputes = Array.isArray(disputesData)
        ? disputesData.filter(
            (item) => String(item.client_id) === String(id)
          )
        : [];

      const documents = Array.isArray(documentsData)
        ? documentsData.filter(
            (item) => String(item.client_id) === String(id)
          )
        : [];

      const creditReports = Array.isArray(reportsData)
        ? reportsData.filter(
            (item) => String(item.client_id) === String(id)
          )
        : [];

      setStats({
        letters: letters.length,
        disputes: disputes.length,
        documents: documents.length,
        creditReports: creditReports.length,
      });

      const timeline = [];

      letters.forEach((item) => {
        timeline.push({
          type: "Letter Generated",
          date: item.created_at,
          detail:
            item.letter_type ||
            item.template ||
            "Dispute Letter",
        });
      });

      disputes.forEach((item) => {
        timeline.push({
          type: "Dispute Created",
          date: item.created_at,
          detail:
            item.creditor ||
            item.bureau ||
            "Credit Bureau",
        });
      });

      documents.forEach((item) => {
        timeline.push({
          type: "Document Uploaded",
          date: item.created_at,
          detail:
            item.document_type ||
            item.filename ||
            "Document",
        });
      });

      creditReports.forEach((item) => {
        timeline.push({
          type: "Credit Report Imported",
          date: item.created_at,
          detail:
            item.filename ||
            item.file_name ||
            "Credit Report",
        });
      });

      timeline.sort(
        (a, b) =>
          new Date(b.date || 0) -
          new Date(a.date || 0)
      );

      setActivity(timeline);
    } catch (err) {
      setError(
        err?.message ||
          "Failed to load the client profile."
      );
    } finally {
      setLoading(false);
    }
  }, [id, applyClientToForm]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  function handleChange(event) {
    const { name, value } = event.target;

    let nextValue = value;

    if (name === "state") {
      nextValue = value.toUpperCase().slice(0, 2);
    }

    if (name === "ssn_last4") {
      nextValue = value.replace(/\D/g, "").slice(0, 4);
    }

    setForm((current) => ({
      ...current,
      [name]: nextValue,
    }));
  }

  async function handleSave(event) {
    event.preventDefault();

    if (!form.first_name.trim()) {
      setError("First name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        full_name:
          `${form.first_name} ${form.last_name}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip_code: form.zip_code.trim(),
        date_of_birth: form.date_of_birth.trim(),
        ssn_last4: form.ssn_last4.trim(),
        notes: form.notes.trim(),
      };

      const updated = await updateClient(id, payload);
      const normalized = normalizeClient(updated);

      setClient(normalized);
      applyClientToForm(normalized);
      setSuccessMessage(
        "Client information saved successfully."
      );
    } catch (err) {
      setError(
        err?.message ||
          "Failed to save client information."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div
        style={{
          padding: "40px",
          fontSize: "18px",
          fontWeight: "700",
        }}
      >
        Loading client profile...
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "40px",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          background: "white",
          borderRadius: "24px",
          padding: "32px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
        }}
      >
        <Button
          variant="ghost"
          onClick={() => navigate("/clients")}
          style={{ marginBottom: "24px" }}
        >
          ← Back To Clients
        </Button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                color: "#64748b",
                fontWeight: "800",
                marginBottom: "8px",
              }}
            >
              Client ID #{id}
            </div>

            <h1
              style={{
                fontSize: "48px",
                fontWeight: "900",
                color: "#0f172a",
                margin: 0,
              }}
            >
              {client?.full_name || "Client Profile"}
            </h1>

            <div
              style={{
                marginTop: "10px",
                color: "#64748b",
                fontSize: "16px",
              }}
            >
              {client?.email || "No email on file"}
            </div>
          </div>
        </div>

        {error ? (
          <div
            style={{
              marginTop: "24px",
              padding: "16px 18px",
              borderRadius: "16px",
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#b91c1c",
              fontWeight: "700",
            }}
          >
            {error}
          </div>
        ) : null}

        {successMessage ? (
          <div
            style={{
              marginTop: "24px",
              padding: "16px 18px",
              borderRadius: "16px",
              border: "1px solid #bbf7d0",
              background: "#f0fdf4",
              color: "#166534",
              fontWeight: "800",
            }}
          >
            {successMessage}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "18px",
            marginTop: "28px",
            marginBottom: "32px",
          }}
        >
          <StatCard title="Letters" value={stats.letters} />
          <StatCard title="Disputes" value={stats.disputes} />
          <StatCard title="Documents" value={stats.documents} />
          <StatCard
            title="Credit Reports"
            value={stats.creditReports}
          />
        </div>

        <form
          onSubmit={handleSave}
          style={{
            marginBottom: "32px",
            background: "#fffaf5",
            border: "1px solid #fed7aa",
            borderRadius: "24px",
            padding: "26px",
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "900",
              color: "#0f172a",
              marginBottom: "6px",
            }}
          >
            Edit Client Information
          </div>

          <div
            style={{
              color: "#64748b",
              marginBottom: "24px",
              lineHeight: 1.6,
            }}
          >
            These details are used in dispute letters and
            client records.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(230px, 1fr))",
              gap: "18px",
            }}
          >
            <InputField
              label="First Name"
              name="first_name"
              value={form.first_name}
              onChange={handleChange}
            />

            <InputField
              label="Last Name"
              name="last_name"
              value={form.last_name}
              onChange={handleChange}
            />

            <InputField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
            />

            <InputField
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />

            <InputField
              label="Street Address"
              name="address"
              value={form.address}
              onChange={handleChange}
            />

            <InputField
              label="City"
              name="city"
              value={form.city}
              onChange={handleChange}
            />

            <InputField
              label="State"
              name="state"
              value={form.state}
              onChange={handleChange}
              maxLength={2}
              placeholder="CA"
            />

            <InputField
              label="ZIP Code"
              name="zip_code"
              value={form.zip_code}
              onChange={handleChange}
            />

            <InputField
              label="Date of Birth"
              name="date_of_birth"
              type="date"
              value={form.date_of_birth}
              onChange={handleChange}
            />

            <InputField
              label="SSN Last 4"
              name="ssn_last4"
              value={form.ssn_last4}
              onChange={handleChange}
              maxLength={4}
              placeholder="1234"
            />
          </div>

          <label
            style={{
              display: "grid",
              gap: "8px",
              marginTop: "18px",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: "800",
                color: "#334155",
              }}
            >
              Notes
            </span>

            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={5}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "14px",
                borderRadius: "14px",
                border: "1px solid #cbd5e1",
                background: "white",
                color: "#0f172a",
                fontSize: "15px",
                resize: "vertical",
              }}
            />
          </label>

          <Button
            type="submit"
            disabled={saving}
            style={{ marginTop: "22px" }}
          >
            {saving ? "Saving Changes..." : "Save Changes"}
          </Button>
        </form>

        <div
          style={{
            marginBottom: "32px",
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "24px",
            padding: "24px",
          }}
        >
          <div
            style={{
              fontSize: "28px",
              fontWeight: "800",
              color: "#0f172a",
              marginBottom: "20px",
            }}
          >
            Activity Timeline
          </div>

          {activity.length === 0 ? (
            <div style={{ color: "#64748b" }}>
              No activity yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "14px" }}>
              {activity.map((item, index) => (
                <div
                  key={`${item.type}-${item.date}-${index}`}
                  style={{
                    padding: "16px",
                    borderRadius: "18px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#0f172a",
                    }}
                  >
                    {item.type}
                  </div>

                  <div
                    style={{
                      fontSize: "15px",
                      color: "#475569",
                      marginTop: "6px",
                    }}
                  >
                    {item.detail}
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      color: "#94a3b8",
                      marginTop: "6px",
                    }}
                  >
                    {item.date || "No Date"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginBottom: "32px",
            background: "linear-gradient(135deg, #fff7ed, #fffbeb)",
            border: "1px solid #fdba74",
            borderRadius: "24px",
            padding: "26px",
            boxShadow: "0 10px 30px rgba(234,88,12,0.06)",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: "900",
              color: "#9a3412",
              letterSpacing: "1.4px",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            Credit Monitoring
          </div>

          <div
            style={{
              fontSize: "26px",
              fontWeight: "900",
              color: "#9a3412",
              marginBottom: "10px",
            }}
          >
            IdentityIQ Credit Monitoring
          </div>

          <div
            style={{
              fontSize: "16px",
              color: "#7c2d12",
              lineHeight: 1.7,
              marginBottom: "18px",
              maxWidth: "760px",
            }}
          >
            Get all 3 credit reports — Equifax, Experian, and
            TransUnion — for <strong>$1 to sign up</strong>,
            then <strong>$32.82/month</strong> for ongoing
            monitoring.
          </div>

          <a
            href={IDENTITYIQ_LINK}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-block",
              padding: "13px 18px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #ea580c, #f97316)",
              color: "#ffffff",
              textDecoration: "none",
              fontWeight: "900",
              fontSize: "15px",
              boxShadow: "0 6px 18px rgba(234,88,12,0.18)",
            }}
          >
            Get All 3 Credit Reports for $1
          </a>

          <div
            style={{
              marginTop: "14px",
              color: "#9a3412",
              fontSize: "12px",
              lineHeight: 1.5,
              maxWidth: "760px",
            }}
          >
            IdentityIQ is a third-party credit monitoring
            service. Pricing and terms are subject to
            IdentityIQ&apos;s current offer.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="secondary"
            onClick={() =>
              navigate(`/documents?client_id=${id}`)
            }
          >
            Documents
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              navigate(`/credit-reports?client_id=${id}`)
            }
          >
            Credit Reports
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              navigate(`/letters?client_id=${id}`)
            }
          >
            Letters
          </Button>

          <Button
            variant="secondary"
            onClick={() =>
              navigate(`/disputes?client_id=${id}`)
            }
          >
            Disputes
          </Button>
        </div>
      </div>
    </div>
  );
}
